import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Store, Crown, ArrowLeft, TrendingUp } from "lucide-react";

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function BranchKpis() {
  const { user, companyId } = useAuth();
  const [rows, setRows] = useState(null);
  const [gcur, setGcur] = useState("SYP");

  const canSee = user?.role === "super_admin" || user?.role === "company_manager";

  useEffect(() => {
    if (!canSee || !companyId) return;
    let alive = true;
    api.get("/branches/consolidated", { params: { from_date: monthStart(), to_date: todayISO() } })
      .then(({ data }) => { if (!alive) return; setRows(data.rows || []); setGcur(data.base_currency || "SYP"); })
      .catch(() => {});
    return () => { alive = false; };
  }, [canSee, companyId]);

  if (!canSee || !rows || rows.length <= 1) return null;

  const totalProfit = rows.reduce((s, r) => s + (r.profit || 0), 0);
  const best = rows.reduce((a, b) => (b.profit > (a?.profit ?? -Infinity) ? b : a), null);

  return (
    <Card className="p-5" data-testid="dashboard-branch-kpis">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">أداء الفروع — هذا الشهر</div>
          <div className="mt-1 text-lg font-heading font-bold flex items-center gap-2">
            <Store className="w-5 h-5 text-primary" /> {rows.length} فروع · صافي ربح {fmtMoney(totalProfit, gcur)}
          </div>
        </div>
        <Link to="/branches" className="text-xs text-primary hover:underline flex items-center gap-0.5">التقرير الموحّد <ArrowLeft className="w-3 h-3" /></Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => (
          <div key={r.company_id} className={`p-3 rounded-lg border ${best && r.company_id === best.company_id ? "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300" : "bg-background"}`} data-testid={`branch-kpi-${r.company_id}`}>
            <div className="flex items-center gap-1.5 font-semibold truncate">
              {r.is_head ? <Crown className="w-4 h-4 text-amber-500 shrink-0" /> : <Store className="w-4 h-4 text-muted-foreground shrink-0" />}
              <span className="truncate" title={r.name}>{r.name}</span>
              {best && r.company_id === best.company_id && <span className="text-[9px] bg-emerald-600 text-white px-1 py-0.5 rounded shrink-0">الأعلى ربحاً</span>}
            </div>
            <div className="flex items-center justify-between mt-2 text-sm">
              <span className="text-muted-foreground text-xs">مبيعات</span>
              <span className="tabular-nums font-bold text-emerald-700">{fmtMoney(r.sales || 0, r.base_currency)}</span>
            </div>
            <div className="flex items-center justify-between mt-0.5 text-sm">
              <span className="text-muted-foreground text-xs">ربح</span>
              <span className={`tabular-nums font-bold ${(r.profit || 0) < 0 ? "text-destructive" : "text-primary"}`}>{fmtMoney(r.profit || 0, r.base_currency)}</span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
